import { useEffect, useState } from "react";
import type { GalleryImage } from "@repo/fetch-repository/products";
import { cn } from "../../../libs/utils";
import VideoPlayer from "../../common/video-player";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "../../ui/carousel";

export interface ProductDetailProps {
  image?: string;
  title?: string;
  detail?: string;
  gallery_images?: GalleryImage[];
  id: number;
  video: {
    data:
      | {
          id: number;
          title: string;
          type: string;
          ext: string;
          description: string | null;
          url: string;
          alt: string | null;
          caption: string | null;
          created_at: string;
          updated_at: string;
        }
      | [];
  };
}

export function ProductDetail(props: ProductDetailProps): React.JSX.Element {
  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);
  const [thumbnailApi, setThumbnailApi] = useState<CarouselApi | null>(null);

  useEffect(() => {
    if (!mainApi) {
      return;
    }

    mainApi.on("select", () => {
      const index = mainApi.selectedScrollSnap();
      thumbnailApi?.scrollTo(index);
    });
  }, [mainApi, thumbnailApi]);

  return (
    <div className="w-full h-full grid grid-cols-1 lg:grid-cols-5 gap-9">
      <div className="flex flex-col gap-5 col-span-1 lg:col-span-2">
        <Carousel className="w-full" setApi={setMainApi}>
          <CarouselContent className="w-full">
            {props.image ? (
              <CarouselItem className="basis-full">
                {props.image.includes("mp4") ? (
                  <VideoPlayer link={props.image || ""} />
                ) : (
                  <div className="col-span-1 lg:col-span-2">
                    <img
                      alt=""
                      className="w-full lg:h-[360px] h-[227px] object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "/images/placeholder.png";
                      }}
                      src={props.image || "/images/placeholder.png"}
                    />
                  </div>
                )}
              </CarouselItem>
            ) : null}
            {props.gallery_images?.length
              ? props.gallery_images.map((img, index) => (
                  <CarouselItem className="basis-full" key={index}>
                    <img
                      alt={img.alt || ""}
                      className="w-full lg:h-[360px] h-[227px] object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "/images/placeholder.png";
                      }}
                      src={img.url}
                    />
                  </CarouselItem>
                ))
              : null}
          </CarouselContent>
        </Carousel>
        <div className="flex gap-2 w-full">
          <Carousel setApi={setThumbnailApi}>
            <CarouselContent>
              {/* {props.image ? (
                <CarouselItem className="lg:basis-1/4 md:basis-1/3 basis-full">
                  <button
                    className="w-full h-[66.4px] md:h-[100px]"
                    onClick={() => {
                      mainApi?.scrollTo(0);
                    }}
                    type="button"
                  >
                    {props.image.includes("mp4") ? (
                      <VideoPlayer link={props.image || ""} />
                    ) : (
                      <div className="col-span-1 lg:col-span-2">
                        <img
                          alt=""
                          className="object-cover object-center hover:opacity-80 transition-opacity h-full"
                          onError={(e) => {
                            e.currentTarget.src = "/images/placeholder.png";
                          }}
                          src={props.image || "/images/placeholder.png"}
                        />
                      </div>
                    )}
                  </button>
                </CarouselItem>
              ) : null} */}
              {props.gallery_images?.length
                ? props.gallery_images.map((image, index) => (
                    <CarouselItem
                      className="lg:basis-1/4 md:basis-1/3 basis-full"
                      key={image.id}
                    >
                      <button
                        className="w-full h-[66.4px] md:h-[100px]"
                        onClick={() => {
                          mainApi?.scrollTo(index + (props.image ? 1 : 0));
                        }}
                        type="button"
                      >
                        <img
                          alt={`${image.alt || ""} thumbnail ${image.id}`}
                          className="object-cover object-center hover:opacity-80 transition-opacity h-full"
                          onError={(e) => {
                            e.currentTarget.src = "/images/placeholder.png";
                          }}
                          src={image.url}
                        />
                      </button>
                    </CarouselItem>
                  ))
                : null}
            </CarouselContent>
          </Carousel>
        </div>
        {!Array.isArray(props.video.data) ? (
          <div className="rounded-sm lg:h-72 h-40">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- disable */}
            <video
              autoPlay
              className="w-full h-full object-cover"
              controls
              loop
              src={props.video.data.url}
            />
          </div>
        ) : null}
      </div>
      {props.detail ? (
        <div className="col-span-1 lg:col-span-3 flex flex-col gap-6">
          <div className="col-span-1 lg:col-span-3">
            <h1 className="text-4xl font-helvetica font-[100] rtl:font-medium rtl:font-doran">
              {props.title}
            </h1>
          </div>
          <div
            className={cn(
              "font-helvetica font-[100] rtl:font-medium rtl:font-doran max-w-full col-span-1 h-full w-full",
              props.image ? "lg:col-span-3" : "lg:col-span-5"
            )}
            dangerouslySetInnerHTML={{ __html: props.detail }}
          />
        </div>
      ) : null}
    </div>
  );
}
ProductDetail.displayName = "ProductDetail";
